/**
 * useEmbedAuth - Unified embed token authentication hook
 *
 * Exchanges the SINGLE-USE, short-lived (~120s) embed handoff token (OTT) for
 * a session, then persists that session so the burned token is never needed
 * again.
 *
 * Shared by the vendor (공급사) and buyer (발주기관) embed entry points. Each app
 * owns its own Supabase client singleton, so the setter is injected via
 * `options.setSupabaseAuth` — everything else is identical, and deliberately so:
 * the buyer page previously re-implemented step 1 alone (no persistence, no
 * dedupe) and every second load of an embed URL hit a burned token.
 *
 * Boot order:
 * 1. TOKEN-FIRST: a token in the URL fragment (#token=..., with a legacy
 *    ?token= fallback) is a fresh handoff minted for a specific account, so
 *    it always wins over a previously persisted session — otherwise a tab
 *    whose sessionStorage still holds another account's session would pin
 *    the old identity (cross-account confusion when the ERP switches
 *    company context). The exchange runs exactly once — concurrent mounts
 *    (React StrictMode double-effects) share one in-flight exchange.
 * 2. If the exchange fails (burned token after an F5/back-nav that kept the
 *    fragment), fall back to the persisted session — but only when it was
 *    minted for the same account as the token; a mismatched session is
 *    cleared up front and the error is surfaced instead.
 * 3. Without a URL token, restore a still-valid session from sessionStorage
 *    ('semo.embed.session.v1') without calling /embed/verify — this keeps
 *    hard refresh, window.open tabs and iframe re-mounts working after the
 *    token has been burned server-side.
 * 4. On success the session is persisted and the token is stripped from the
 *    URL via history.replaceState so it cannot leak or be re-submitted.
 *
 * Internal SPA navigation between embed routes must NOT re-append the token:
 * authentication is carried by the mounted provider + persisted session.
 *
 * Usage:
 * ```tsx
 * const { isAuthenticated, isVerifying, error, userId, retry } = useEmbedAuth({
 *   setSupabaseAuth: (token) => setSupabaseAuth(supabase, token),
 * });
 *
 * if (isVerifying) return <EmbedLoadingState />;
 * if (error) return <EmbedErrorState message={error} onRetry={retry} />;
 * if (!isAuthenticated) return <EmbedUnauthorizedState />;
 * ```
 */

import { useState, useEffect, useCallback } from "react";
import {
  useExternalEmbedControllerVerifyEmbedToken,
  type BuyerInfoDto,
  type EmbedTokenVerifyResponseDto,
  type SupplierInfoDto,
  setAuthToken,
  clearAuthToken,
} from "@semo/api-contracts";
import {
  clearEmbedSession,
  extractEmbedTokenFromLocation,
  getEmbedTokenSubject,
  persistEmbedSession,
  readStoredEmbedSession,
  removeEmbedTokenFromUrl,
  type IStoredEmbedSession,
} from "../embed-session";

/** Applies (or clears, on "") the access token on the caller app's Supabase client. */
export type SetSupabaseAuthFn = (accessToken: string) => void;

export interface EmbedAuthState {
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether verification is in progress */
  isVerifying: boolean;
  /** Error message if verification failed */
  error: string | null;
  /** Full authentication payload from API */
  authPayload: EmbedTokenVerifyResponseDto | null;
  /** User ID from the token */
  userId: string | null;
  /** Supplier ID from the token (supplier tokens only) */
  supplierId: string | null;
  /** Supplier information from the token (supplier tokens only) */
  supplierInfo: SupplierInfoDto | null;
  /** External buyer ID from the token (buyer tokens only) */
  buyerId: string | null;
  /** Buyer organisation profile from the token (buyer tokens only) */
  buyerInfo: BuyerInfoDto | null;
  /** Retry verification (resets state and triggers new verification) */
  retry: () => void;
}

export interface UseEmbedAuthOptions {
  /**
   * Applies the exchanged access token to the app's Supabase client. Injected
   * because each app owns its own client singleton (vendor re-exports a
   * 1-arg setter, buyer calls setSupabaseAuth(supabase, token)).
   */
  setSupabaseAuth: SetSupabaseAuthFn;
  /** Token override (default: auto-extracted from #token= fragment, then ?token=) */
  token?: string | null;
  /** Callback when verification succeeds */
  onSuccess?: (payload: EmbedTokenVerifyResponseDto) => void;
  /** Callback when verification fails */
  onError?: (error: string) => void;
}

const ERROR_MESSAGES = {
  noToken: "토큰이 제공되지 않았습니다.",
  invalidToken: "유효하지 않은 토큰입니다.",
  verificationFailed: "인증에 실패했습니다. 다시 시도해주세요.",
};

/**
 * OTT 교환 결과가 로컬 state 로 전파되지 못한 채 verifying 스피너에 갇힐 때
 * sessionStorage 에 저장된 세션으로 자가 복구를 시도하기까지의 대기 시간(ms).
 *
 * 값의 근거: OTT 교환은 대부분 <500ms 에 끝나므로 정상 흐름에선 이 타이머가 발동 전에
 * authPayload 가 세팅돼 effect 가 조기 종료된다. 1500ms 는 느린 네트워크·StrictMode
 * 이중 마운트로 정말 stuck 된 케이스에만 걸리도록 잡은 마진.
 */
const EMBED_AUTH_SELF_HEAL_DELAY_MS = 1500;

type VerifyMutateAsync = ReturnType<
  typeof useExternalEmbedControllerVerifyEmbedToken
>["mutateAsync"];

// Module-scoped so every mount of the hook (StrictMode double-effects,
// multiple embed components) shares ONE exchange per token — a second POST
// would fail because the token is burned on the first successful verify.
const inFlightExchanges = new Map<
  string,
  Promise<EmbedTokenVerifyResponseDto>
>();

// Test-only: clears the module-scoped dedupe map between test cases.
export function __resetEmbedTokenExchangeForTests(): void {
  inFlightExchanges.clear();
}

async function performExchange(
  token: string,
  mutateAsync: VerifyMutateAsync,
  setSupabaseAuth: SetSupabaseAuthFn,
): Promise<EmbedTokenVerifyResponseDto> {
  const response = await mutateAsync({ data: { token } });
  const payload = response.data;

  if (!payload?.isValid) {
    throw new Error(payload?.message ?? ERROR_MESSAGES.invalidToken);
  }

  if (payload.accessToken) {
    setAuthToken(payload.accessToken);
    setSupabaseAuth(payload.accessToken);
    persistEmbedSession(payload);
  }
  removeEmbedTokenFromUrl();

  return payload;
}

function exchangeEmbedTokenOnce(
  token: string,
  mutateAsync: VerifyMutateAsync,
  setSupabaseAuth: SetSupabaseAuthFn,
): Promise<EmbedTokenVerifyResponseDto> {
  const existing = inFlightExchanges.get(token);
  if (existing) return existing;

  const exchange = performExchange(token, mutateAsync, setSupabaseAuth).finally(
    () => {
      inFlightExchanges.delete(token);
    },
  );
  inFlightExchanges.set(token, exchange);
  return exchange;
}

export function useEmbedAuth(options: UseEmbedAuthOptions): EmbedAuthState {
  const { setSupabaseAuth } = options;

  // Read once at mount: after a successful exchange the token is stripped
  // from the URL, and internal navigation no longer carries it.
  const [urlToken] = useState<string | null>(() =>
    extractEmbedTokenFromLocation(),
  );
  const token = options.token ?? urlToken;

  // Local state (self-contained, no external context dependencies)
  const [authPayload, setAuthPayload] =
    useState<EmbedTokenVerifyResponseDto | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [hasAttempted, setHasAttempted] = useState(false);

  // API mutation hook
  const verifyMutation = useExternalEmbedControllerVerifyEmbedToken();

  useEffect(() => {
    // Guard 1: Already verified successfully
    if (authPayload) {
      return;
    }

    // Guard 2: Already has error (user must retry manually)
    if (authError && hasAttempted) {
      return;
    }

    // Guard 3: Already attempted (prevents loops)
    if (hasAttempted && !authError) {
      return;
    }

    const restoreSession = (session: IStoredEmbedSession): void => {
      setAuthToken(session.accessToken);
      setSupabaseAuth(session.accessToken);
      setAuthPayload(session.verify);
      setAuthError(null);
      options.onSuccess?.(session.verify);
    };

    // No URL token: session-first boot — the OTT was single-use, so a
    // previously exchanged token cannot be re-verified; restore the
    // persisted session instead.
    if (!token) {
      const storedSession = readStoredEmbedSession();
      if (storedSession) {
        restoreSession(storedSession);
        setHasAttempted(true);
        return;
      }
      setAuthError(ERROR_MESSAGES.noToken);
      setHasAttempted(true);
      return;
    }

    // TOKEN-FIRST: the URL token is a fresh handoff minted for a specific
    // account and must win over any persisted session. When the persisted
    // session belongs to a DIFFERENT account than the token's subject
    // (decoded without verification — routing hint only), drop it now so it
    // can never be restored against someone else's token.
    const storedSession = readStoredEmbedSession();
    const tokenSubject = getEmbedTokenSubject(token);
    if (
      storedSession &&
      tokenSubject &&
      storedSession.verify.userId !== tokenSubject
    ) {
      clearEmbedSession();
    }

    // Clear any existing auth token to ensure clean verification (Memory Only)
    clearAuthToken();
    setSupabaseAuth("");

    exchangeEmbedTokenOnce(token, verifyMutation.mutateAsync, setSupabaseAuth)
      .then((payload) => {
        setAuthPayload(payload);
        setAuthError(null);
        options.onSuccess?.(payload);
      })
      .catch((err: unknown) => {
        // Burned token after an F5/back-nav that kept the fragment: fall
        // back to the persisted session. Mismatched-account sessions were
        // cleared above, so this only ever restores the same identity.
        const fallbackSession = readStoredEmbedSession();
        if (fallbackSession) {
          removeEmbedTokenFromUrl();
          restoreSession(fallbackSession);
          return;
        }

        const errorMsg =
          err instanceof Error && err.message
            ? err.message
            : ERROR_MESSAGES.verificationFailed;
        setAuthError(errorMsg);
        setAuthPayload(null);
        options.onError?.(errorMsg);
      })
      .finally(() => {
        setHasAttempted(true);
      });
    // Note: Not including verifyMutation/options to prevent re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authPayload, authError, hasAttempted]);

  // 자가 치유(dev StrictMode / 느린 첫 로드): OTT 교환은 성공 시 세션을
  // sessionStorage 에 저장한다. 그 결과가 이 마운트 상태로 전파되지 못해 verifying
  // 스피너가 멈춘 경우(첫 오픈에서 관찰, 수동 새로고침으로만 풀리던 증상), 저장된
  // 세션으로 스스로 복구해 새로고침 없이도 진입한다. 교차계정 세션은 위에서 이미
  // 정리되므로 교환이 방금 저장한 동일 계정 세션만 복원된다(안전).
  useEffect(() => {
    if (authPayload || authError) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      const session = readStoredEmbedSession();
      if (!session) return;
      setAuthToken(session.accessToken);
      setSupabaseAuth(session.accessToken);
      setAuthPayload(session.verify);
      setAuthError(null);
      setHasAttempted(true);
    }, EMBED_AUTH_SELF_HEAL_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPayload, authError]);

  // Retry action - resets state to trigger new verification (restores from
  // the persisted session when available; a burned token will fail again and
  // the ERP must re-open the embed with a fresh URL).
  const retry = useCallback(() => {
    setAuthPayload(null);
    setAuthError(null);
    setHasAttempted(false);
  }, []);

  // Derived state
  const isAuthenticated = !!authPayload?.isValid && !authError;
  // 결과(payload/error)가 나오면 확정적으로 verifying 종료. mutation.isPending 이
  // StrictMode 언마운트 등으로 stuck true 로 남아도 스피너에 갇히지 않는다.
  const isVerifying =
    !authPayload && !authError && (verifyMutation.isPending || !hasAttempted);

  return {
    isAuthenticated,
    isVerifying,
    error: authError,
    authPayload,
    userId: authPayload?.userId ?? null,
    supplierId: authPayload?.supplierId ?? null,
    supplierInfo: authPayload?.supplierInfo ?? null,
    buyerId: authPayload?.buyerId ?? null,
    buyerInfo: authPayload?.buyerInfo ?? null,
    retry,
  };
}
